import { useCallback, useMemo, useState } from "react";
import { Stepper } from "../molecules";
import { Col, Container, Row } from "react-grid-system";
import styled from "styled-components";
import { CreateProprertyTemplate } from "../templates";
import { Button } from "../atoms";
import {
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from "@/customHooks";
import { useNavigate } from "react-router";
import { useCreatePropertyContext } from "@/customHooks/useCreatePropertyContext";
import textsData from '@/textData/CreatePropertyTexts.json'
import { 
  CreatePropertyMutation, 
  useCreatePropertyMutation,
  useGetFileUploadUrlsMutation,
  FileOriginOption,
  FileTypeOption,
} from "@/graphql/generated";
import { FetchResult } from "@apollo/client";

const StepperContainer = styled(Col)`
  display: flex;
  justify-content: center;
  padding-top: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.md};
`

const ButtonsContainer = styled(Col)`
  margin-bottom: 2rem;
`
export const CreatePropertyPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  
  const steps = useMemo(() => texts.templateText.map((element) => element.step), [])

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const navigate = useNavigate()

  const { state, buttonState, pictures, videos } = useCreatePropertyContext()

  const { open: errorHandle } = useErrorHandlerContext()

  const [getUploadUrls] = useGetFileUploadUrlsMutation()
  const [createProperty, { loading }] = useCreatePropertyMutation()

  const onFinishHandle = useCallback(async () => {
    const onSuccessHandle = (response: FetchResult<CreatePropertyMutation>) => {
      confirmDialogModalOpen({
        title: texts.successDialog.title,
        description:`${texts.successDialog.description} ${response.data?.createProperty.name}`
      })
      navigate(-1)
    }

    try {
      setIsLoading(true);
      let uploadedFilesIds: string[] = [];

      if (pictures.length > 0) {
        const getUploadUrlsResponse = await getUploadUrls({
          variables: {
            inputs: pictures.map((picture) => ({
              contentType: picture.type,
              fileExtension: 'webp',
              fileTypeOption: FileTypeOption.Picture,
              fileOriginOption: FileOriginOption.Property,
              fileSize: picture.size.toString(),
            })),
          },
        });

        const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
        if (!uploadDataList || uploadDataList.length !== pictures.length) {
          throw new Error('No se pudieron obtener las URLs para subir las imágenes.');
        }

        const uploadPromises = uploadDataList.map((uploadData, index) => {
          const picture = pictures[index];
          const uploadUrl = uploadData.uploadUrl ?? '';
          return fetch(uploadUrl, {
            method: 'PUT',
            body: picture,
            headers: { 'Content-Type': picture.type },
          });
        });

        const uploadResponses = await Promise.all(uploadPromises);

        uploadResponses.forEach((response) => {
          if (!response.ok) {
            throw new Error('Falló la subida de una o más imágenes.');
          }
        });

        uploadedFilesIds = uploadDataList.map(uploadData => uploadData.id);
      }

      if (videos.length > 0) {
        const getUploadUrlsResponse = await getUploadUrls({
          variables: {
            inputs: videos.map((video) => ({
              contentType: 'video/mp4',
              fileExtension: 'mp4',
              fileTypeOption: FileTypeOption.Video,
              fileOriginOption: FileOriginOption.Property,
              fileSize: video.size.toString(),
            })),
          },
        });

        const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
        if (!uploadDataList || uploadDataList.length !== videos.length) {
          throw new Error('No se pudieron obtener las URLs para subir los videos.');
        }

        const uploadPromises = uploadDataList.map((uploadData, index) => {
          const video = videos[index];
          const uploadUrl = uploadData.uploadUrl ?? '';
          return fetch(uploadUrl, {
            method: 'PUT',
            body: video,
            headers: { 'Content-Type': 'video/mp4' },
          });
        });

        const uploadResponses = await Promise.all(uploadPromises);

        uploadResponses.forEach((response) => {
          if (!response.ok) {
            throw new Error('Falló la subida de uno o más videos.');
          }
        });

        uploadedFilesIds = uploadedFilesIds.concat(uploadDataList.map(uploadData => uploadData.id));
      }

      const createdProperty = await createProperty({ 
        variables: { 
          input: { 
            ...state, 
            ...(uploadedFilesIds.length > 0 && { uploadedFilesIds })
          } as any
        } 
      });
      onSuccessHandle(createdProperty)
    } catch (error: any) {
      errorHandle(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    pictures,
    videos,
    state,
    getUploadUrls,
    createProperty,
    errorHandle,
    confirmDialogModalOpen,
    texts.successDialog,
    navigate,
  ]);

  return (
    <Container>
      <Row>
        <StepperContainer>
          <Stepper
            steps={steps}
            currentStep={currentPage}
          />
        </StepperContainer>
      </Row>
      <Row>
        <Col>
          <CreateProprertyTemplate currentTemplate={currentPage} />
        </Col>
      </Row>
      <Row justify="center">
        <Col xs="content">
          <Button
            secondary
            onClick={() => {
              if (currentPage > 0) setCurrentPage((prev) => prev - 1)
              else navigate(-1)
            }}
            disabled={isLoading}
          >
            {texts.cancelButton}
          </Button>
        </Col>
        <ButtonsContainer xs="content">
          <Button
            onClick={() => {
              if (currentPage < steps.length - 1) setCurrentPage((prev) => prev + 1)
              else onFinishHandle()
            }}
            disabled={buttonState}
            isLoading={loading || isLoading}
          >
            {texts.confirmButton}
          </Button>
        </ButtonsContainer>
      </Row>
    </Container>
  )
}